import React, { useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import ComingSoonPage from '../../components/common/501';
import DateRangeFilter from '../../components/common/DateRangeFilter';
import dayjs from 'dayjs';

import { SalesReportProvider } from '../../contexts/SalesReportContext';
import { LeadReportProvider } from '../../contexts/LeadReportContext';
import { DealLostReportProvider } from '../../contexts/DealLostReportContext';
import { AgentReportProvider } from '../../contexts/AgentReportContext';
// import AgentReport from './AgentReport';
import AgentReportDetail from './AgentReportDetail';
import AgentReportPage from './AgentReportPage';
import LeadReportDetails from './lead-report-details/LeadReportDetails';
import AgentActivitySummaryReportPage from './AgentActivitySummaryReportPage';
import AgentCallDetailedReportPage from './AgentCallDetailedReportPage';
import AgentLeadStatusReportPage from './AgentLeadStatusReportPage';
import AgentVisitsMeetingsReportPage from './AgentVisitsMeetingsReportPage';
import AgentLeadSourceStatusReportPage from './AgentLeadSourceStatusReportPage';
import LeadSourcePerformanceReportPage from './LeadSourcePerformanceReportPage';
import ProjectLeadStatusReportPage from './ProjectLeadStatusReportPage';
import CpLeadStatusReportPage from './CpLeadStatusReportPage';

// ------------------ COMMON DATE CONFIG ------------------
const defaultDateRange = {
  filter: { id: 'This Month', label: 'This Month' },
  currentStart: dayjs().startOf('month'),
  currentEnd: dayjs().endOf('month'),
  compareStart: null,
  compareEnd: null,
};

const agentDefaultDateRange = {
  filter: { id: 'Today', label: 'Today' },
  currentStart: dayjs().startOf('day'),
  currentEnd: dayjs().endOf('day'),
  compareStart: null,
  compareEnd: null,
};

// ------------------ PERIOD TYPE HELPER ------------------
const getPeriodType = (dateRangeState) => {
  if (!dateRangeState?.filter?.id) return 'custom';
  const filterId = dateRangeState.filter.id.toLowerCase();
  if (filterId.includes('today')) return 'today';
  if (filterId.includes('week')) return 'this_week';
  if (filterId.includes('month')) return 'this_month';
  if (filterId.includes('quarter')) return 'this_quarter';
  if (filterId.includes('year')) return 'this_year';
  return 'custom';
};

// ------------------ MAIN REPORT BASE ------------------
const ReportBase = () => {
  const location = useLocation();
  const isAgentReportsPage = location.pathname === '/reports/agents';
  const initialDateRange = isAgentReportsPage ? agentDefaultDateRange : defaultDateRange;

  const [dateRangeState, setDateRangeState] = useState(initialDateRange);
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [teams, setTeams] = useState([]);

  const handleDateRangeChange = (rangeObj) => setDateRangeState(rangeObj);
  const periodType = getPeriodType(dateRangeState);

  // Handler for Teams filter selection
  const handleTeamProjectFilter = (selectedTeamIds) => {
    console.log('Teams filter selected:', selectedTeamIds);
    setSelectedTeams(selectedTeamIds || []);
    // TODO: Implement team filter functionality to filter reports by selected teams
  };

  const hideDatePickerRoutes = ['/sales', '/deal-lost'];
  const shouldShowDatePicker = !hideDatePickerRoutes.some((path) =>
    location.pathname.startsWith(path)
  ) && !location.pathname.match(/\/agent\/\d+/);
  
  // Check if we're on the leads report page to show team filter
  const isLeadsReportPage = location.pathname === '/reports/leads';
  
  // Show team filter for both agent and leads report pages
  const showTeamFilter = isAgentReportsPage || isLeadsReportPage;

  return (
    <Box
      sx={{
        flexGrow: 1,
        minHeight: '100vh',
        pt: 2,
        pb: 3,
        px: { xs: 2, sm: 3 },
      }}
    >
      {shouldShowDatePicker && (
        <Box sx={{ mb: 3, mt: 0 }}>
          <Box sx={{
            width: '100%',
            backgroundColor: 'background.paper',
            borderRadius: 2,
            boxShadow: '0 2px 5px rgba(0,0,0,0.05)',
            p: { xs: 2, sm: 2.5 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 2
          }}>
            <DateRangeFilter
              onChange={handleDateRangeChange}
              dateRangeState={dateRangeState}
              showAllOption={false}
              defaultFilterId={isAgentReportsPage ? 'Today' : undefined}
              isAgentReportsPage={isAgentReportsPage}
              showTeamFilter={showTeamFilter}
              onTeamProjectFilter={handleTeamProjectFilter}
              showExportPDF={false}
              selectedTeams={selectedTeams}
              setTeams={setTeams}
            />
          </Box>
        </Box>
      )}

      <Routes>
        {/* Redirect default /reports → /reports/agent */}
        <Route path="/" element={<Navigate to="/reports/agents" replace />} />

        <Route
          path="agents"
          element={
            <AgentReportProvider>
              <AgentReportPage 
                dateRangeState={dateRangeState} 
                periodType={periodType}
                selectedTeams={selectedTeams}
                teams={teams}
              />
            </AgentReportProvider>
          }
        />
        <Route
          path="agent/:agentId"
          element={
            <AgentReportProvider>
              <AgentReportDetail
                dateRangeState={dateRangeState}
                periodType={periodType}
              />
            </AgentReportProvider>
          }
        />
        <Route
          path="agent-activity-summary"
          element={
            <AgentActivitySummaryReportPage
              dateRangeState={dateRangeState}
              selectedTeams={selectedTeams}
            />
          }
        />
        <Route
          path="agent-call-detailed"
          element={<AgentCallDetailedReportPage />}
        />
        <Route
          path="agent-lead-status"
          element={
            <AgentLeadStatusReportPage
              dateRangeState={dateRangeState}
              selectedTeams={selectedTeams}
            />
          }
        />
        <Route
          path="agent-visits-meetings"
          element={
            <AgentVisitsMeetingsReportPage
              dateRangeState={dateRangeState}
              selectedTeams={selectedTeams}
            />
          }
        />
        <Route
          path="agent-lead-source-status"
          element={<AgentLeadSourceStatusReportPage />}
        />
        <Route
          path="lead-source-performance"
          element={
            <LeadSourcePerformanceReportPage
              dateRangeState={dateRangeState}
              selectedTeams={selectedTeams}
            />
          }
        />
        <Route
          path="project-lead-status"
          element={
            <ProjectLeadStatusReportPage
              dateRangeState={dateRangeState}
              selectedTeams={selectedTeams}
            />
          }
        />
        <Route
          path="cp-lead-status"
          element={
            <CpLeadStatusReportPage
              dateRangeState={dateRangeState}
              selectedTeams={selectedTeams}
            />
          }
        />
        <Route
          path="sales"
          element={
            <ComingSoonPage
              title="Sales Reports Coming Soon"
              message="Comprehensive sales analytics and performance tracking tools are in development."
              progress={80}
              estimatedRelease="Q1 2024"
            />
          }
        />
        <Route
          path="leads"
          element={
            <LeadReportProvider>
              <LeadReportDetails 
                dateRangeState={dateRangeState} 
                periodType={periodType}
                selectedTeams={selectedTeams}
              />
            </LeadReportProvider>
          }
        />
        <Route
          path="deal-lost"
          element={
            <ComingSoonPage
              title="Deal Lost Reports Coming Soon"
              message="Deal lost tracking and analysis tools are in development."
              progress={75}
              estimatedRelease="Q1 2024"
            />
          }
        />
        <Route
          path="*"
          element={
            <ComingSoonPage
              title="Reports Coming Soon"
              message="This report feature is currently under development and will be available soon."
              progress={75}
              estimatedRelease="Q1 2024"
            />
          }
        />
      </Routes>
    </Box>
  );
};

export default ReportBase;
